import { getRenderingEngine } from "@cornerstonejs/core";
import {
  CrosshairsTool,
  ToolGroupManager,
  Enums as csToolsEnums,
} from "@cornerstonejs/tools";
import getIds from "lib/cornerstone/helpers/getIds";
import { proxy } from "valtio";
import { utilities as csUtils } from "@cornerstonejs/core";
import { setProgrammaticVOIChange } from "lib/cornerstone/helpers/voiState";

const { MouseBindings } = csToolsEnums;

interface WindowLevel {
  width: number;
  center: number;
}

interface SettingsState {
  windowLevel: WindowLevel;
  defaultWindowLevel: WindowLevel;
  roiOpacity: number;
  showOrientationLabel: boolean;
}

const settingsState = proxy<SettingsState>({
  windowLevel: { width: 20, center: 20 },
  defaultWindowLevel: { width: 500, center: 500 },
  roiOpacity: 100,
  showOrientationLabel: true,
});

// Callback for ROI opacity changes (set by viewer.ts)
let onRoiOpacityChange: ((value: number) => void) | null = null;

export function registerRoiOpacityCallback(callback: (value: number) => void) {
  onRoiOpacityChange = callback;
}

export function updateRoiOpacity(value: number) {
  if (settingsState.roiOpacity === value) return;
  settingsState.roiOpacity = value;
  // Call the registered callback
  onRoiOpacityChange?.(value);
}

export function updateWindowLevel(
  newWindowLevel: Partial<WindowLevel>,
  setDefault: boolean = false,
) {
  const newWidth = newWindowLevel.width ?? settingsState.windowLevel.width;
  const newCenter = newWindowLevel.center ?? settingsState.windowLevel.center;

  // Only update if values actually changed
  if (
    newWidth === settingsState.windowLevel.width &&
    newCenter === settingsState.windowLevel.center
  ) {
    return;
  }

  settingsState.windowLevel = { width: newWidth, center: newCenter };

  if (setDefault) {
    settingsState.defaultWindowLevel = { width: newWidth, center: newCenter };
  }

  // Directly call renderer update (no subscribe needed)
  updateRendererWindowLevel(newWidth, newCenter);
}

export function toogleOrientationLabel() {
  settingsState.showOrientationLabel = !settingsState.showOrientationLabel;
}

function updateRendererWindowLevel(width: number, center: number) {
  const { renderingEngineId, volumeId } = getIds();
  const renderingEngine = getRenderingEngine(renderingEngineId);

  if (!renderingEngine) {
    return;
  }

  // Convert window width/center to lower/upper range
  const { lower, upper } = csUtils.windowLevel.toLowHighRange(width, center);

  // Set flag to prevent voiModifiedListener from triggering infinite loop
  setProgrammaticVOIChange(true);

  const viewports = renderingEngine.getViewports();

  viewports.forEach((viewport: any) => {
    try {
      if (typeof viewport.setProperties === "function") {
        viewport.setProperties({ voiRange: { lower, upper } });
      }
    } catch (e) {
      // Try VTK actor fallback
      try {
        const actor = viewport.getActor?.(volumeId)?.actor;
        if (actor?.getProperty) {
          const rgbTransferFunction = actor
            .getProperty()
            .getRGBTransferFunction?.(0);
          rgbTransferFunction?.setMappingRange?.(lower, upper);
        }
      } catch (e2) {
        // silent
      }
    }
  });

  renderingEngine.render();

  // Reset flag after a short delay to allow events to fire
  setTimeout(() => {
    setProgrammaticVOIChange(false);
  }, 50);
}

export function disableCrossHair() {
  const { toolGroupId } = getIds();
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);

  if (!toolGroup) {
    console.warn("No Toolgroup");
    return;
  }

  const { mode } = toolGroup.getToolOptions(CrosshairsTool.toolName);

  if (mode === "Active") {
    toolGroup.setToolDisabled(CrosshairsTool.toolName);
  } else {
    toolGroup.setToolActive(CrosshairsTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Primary }],
    });
  }
}

export default settingsState;
